import { Col, Row, Spin, Card, Typography } from "antd";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { useEffect } from "react";
import { fetchStudentTable } from "redux/reducers/StudentsTableSlice";
import { Helmet } from "react-helmet";
import SRSStatisticsCard from "components/common/statistics/srs-statistics-card";
import EASSearchCard from "components/school/eas/eas-search-card";
import AccessSchemesCard from "components/common/statistics/access-schemes-card";
import KeyDates from "components/common/key-dates";
import Restricted from "components/common/restricted";
import { PERMISSIONS } from "permissions/app-permissions";
import { fetchSRSApplicants } from "redux/reducers/SRSApplicantSlice";
import AuthHelper from "components/auth/helpers";
import { APP_ROLES, WHITELABEL } from "utils/Constants";
import { AppRolesValuesType, Env } from "utils/Types";

const { Title } = Typography;

const Dashboard: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const whitelabel = useAppSelector(state => state.properties.whiteLabel);
  const { env } = useAppSelector(state => state.globalUI);
  const { givenName1, schoolName } = useAppSelector(state => state.schoolContact.data);
  const { statistics, isLoading } = useAppSelector(state => state.students);
  const { totalApplicants, totalAppliedWhitelabel } = statistics;
  const { isSrsOpen, srsClosingDate, isEasOpen, easClosingDate, isSrsOpenOS, srsClosingDateOS } =
    useAppSelector(state => state.properties.closingData);
  const roles = AuthHelper.getRoles();
  const isAdminOrSRS = roles.some(
    (x: AppRolesValuesType) => x === APP_ROLES.ADMIN || x === APP_ROLES.SRS
  );
  const isOS = env === Env.INTERNATIONAL;

  useEffect(() => {
    window.scrollTo(0, 0);
    document.getElementById("skip-main-wrap")?.focus();
  }, []);

  useEffect(() => {
    dispatch(
      fetchStudentTable({
        source: env
      })
    );
    // Fetch srs applicants for the dashboard metrics (admin and SRS role only)
    isAdminOrSRS &&
      dispatch(
        fetchSRSApplicants({
          source: env
        })
      );
  }, [env]);

  return (
    <>
      <Helmet>
        <title>{t("helmet.dashboard.title")}</title>
        <meta name="description" content={t("helmet.dashboard.meta") || ""} />
      </Helmet>
      {isLoading ? (
        <Spin size="large" className="center-content" style={{ height: "50vh" }} />
      ) : (
        <>
          <div style={{ display: "flex", marginBottom: 32 }}>
            <div>
              <Title style={{ marginBottom: 8, fontSize: 24, lineHeight: "32px" }}>
                {t("dashboard.title")}
                <span className="primary-color">{givenName1}</span>
              </Title>
              <p className="primary-color display-tab-typo">{schoolName}</p>
            </div>
          </div>

          <Row gutter={16}>
            <Col sm={24} xl={18}>
              {/* top section is shown for all user roles */}
              <Row gutter={16} style={{ marginBottom: "2rem" }}>
                <Col span={12}>
                  <Card style={{ height: "100%" }}>
                    <h2 className="display-p-bold-typo">{t("dashboard.card-1")}</h2>
                    <div className="display-lar-typo ">{totalApplicants}</div>
                  </Card>
                </Col>
                <Col span={12}>
                  <Card style={{ height: "100%" }}>
                    {whitelabel === WHITELABEL.utas && (
                      <h2 className="display-p-bold-typo">{t("dashboard.card-2")}</h2>
                    )}
                    {whitelabel === WHITELABEL.uac && env === Env.DOMESTIC && (
                      <h2 className="display-p-bold-typo">{t("dashboard.card-2-ug")}</h2>
                    )}
                    {whitelabel === WHITELABEL.uac && env === Env.INTERNATIONAL && (
                      <h2 className="display-p-bold-typo">{t("dashboard.card-2-os")}</h2>
                    )}
                    <div className="display-lar-typo ">{totalAppliedWhitelabel}</div>
                  </Card>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={24} xl={0}>
                  <Card
                    title={<h2 className="card-title">{t("dashboard.accessScheme.keydates")}</h2>}
                    style={{ marginBottom: 20 }}
                  >
                    {isOS ? (
                      <KeyDates
                        isOpen={isSrsOpenOS}
                        title={t("dashboard.accessScheme.srs-label")}
                        closingDate={srsClosingDateOS}
                        marginBtm={16}
                      />
                    ) : (
                      <KeyDates
                        isOpen={isSrsOpen}
                        title={t("dashboard.accessScheme.srs-label")}
                        closingDate={srsClosingDate}
                        marginBtm={16}
                      />
                    )}

                    <Restricted to={PERMISSIONS.DASHBOARD.INFO_BOX.EAS}>
                      <KeyDates
                        isOpen={isEasOpen}
                        title={t("dashboard.accessScheme.eas-sm-label")}
                        closingDate={easClosingDate}
                        marginBtm={0}
                      />
                    </Restricted>
                  </Card>
                </Col>
              </Row>

              {/* bottom hand section is dependent on user role */}
              <Row gutter={16} wrap={true}>
                <Restricted to={PERMISSIONS.DASHBOARD.SRS_STATISTICS_CARD}>
                  <Col span={24}>
                    <SRSStatisticsCard />
                  </Col>
                </Restricted>
              </Row>
              {env === Env.DOMESTIC && (
                <Row gutter={16} wrap={true}>
                  <Restricted to={PERMISSIONS.DASHBOARD.EAS_SEARCH_CARD}>
                    <Col span={24}>
                      <EASSearchCard desktop={true} redirect={true} />
                    </Col>
                  </Restricted>
                </Row>
              )}
            </Col>
            <Col sm={24} xl={6} style={{ width: "100%" }}>
              <AccessSchemesCard />
            </Col>
          </Row>
        </>
      )}
    </>
  );
};

export default Dashboard;
